/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useState } from "react";
import { makePost, makePatch, signUpload } from "@culturehq/client";
import { Form, ModalDialog, StringField, SubmitButton, NumberField, SelectField, TextField } from "@culturehq/components";
import { useDepartments, useLocations, useInterests, useSkills, useValues, useCompanies, useProfile } from "app/context";

import { useDoor, useFieldsLabels, useTryCatch } from "app/hooks";
import { canViewCompaniesField } from "app/permissions";
import { Trans, getDepartmentFieldCategory, getCompanyFieldCategory, t, isRTX } from "app/utils";

import ErrorMessage from "components/common/ErrorMessage";
import tagsTypes, { getCategoryLabel, getIcon } from "constants/tagsTypes";
import ChqModalDialog from "components/common/ChqModalDialog";
import ApproveForJobMappingButton from "../stories/ApproveForJobMappingButton";
import { allowedMedia } from "./SubPromptModal";

const makeSelectOptions = (entities, labelKey, valueKey, categoryIcon, icon, category) => {
  const selectOptions = [];

  Object.keys(entities).forEach(entityId => {
    const { [labelKey]: label, [valueKey]: value } = entities[entityId];
    selectOptions.push({ label, value, categoryIcon, icon, category });
  });

  return selectOptions.sort((a, b) => a.label.localeCompare(b.label));
};

const useSelectOptions = () => {
  const { profile } = useProfile();
  const { companies, onAllCompaniesRequested } = useCompanies();
  const { departments, onAllDepartmentsRequested } = useDepartments();
  const { interests, onAllInterestsRequested } = useInterests();
  const { locations, onAllLocationsRequested } = useLocations();
  const { skills, onAllSkillsRequested } = useSkills();
  const { organizationValues, onAllOrganizationValuesRequested } = useValues();

  useEffect(
    () => {
      if (profile && canViewCompaniesField(profile.organization)) {
        onAllCompaniesRequested();
      }
    }, [profile, onAllCompaniesRequested]
  );

  useEffect(() => {
    onAllInterestsRequested();
    onAllLocationsRequested();
    onAllDepartmentsRequested();
    onAllSkillsRequested();
    onAllOrganizationValuesRequested();
  }, [onAllDepartmentsRequested, onAllInterestsRequested,
    onAllLocationsRequested, onAllSkillsRequested, onAllOrganizationValuesRequested]);

  const interestsOptions = makeSelectOptions(interests, "name", "id", "heart-ol", "heart", tagsTypes.INTERESTS);
  const locationOptions = makeSelectOptions(locations, "name", "id", "loc-ol", "ios-location", tagsTypes.LOCATIONS);
  const departmentOptions = makeSelectOptions(departments, "name", "id", "ios-people-outline", "ios-people", getDepartmentFieldCategory(profile.organization.name));
  const skillOptions = makeSelectOptions(skills, "name", "id", "hammer", "hammer-filled", tagsTypes.SKILLS);
  const orgValuesOptions = makeSelectOptions(organizationValues, "name", "id", "diamond", "diamond-filled", tagsTypes.ORG_VALUES, [], undefined);

  let options = [
    ...orgValuesOptions,
    ...interestsOptions,
    ...locationOptions,
    ...departmentOptions,
    ...skillOptions
  ];

  if (profile && canViewCompaniesField(profile.organization)) {
    const companyOptions = makeSelectOptions(companies, "name", "id", "building", "building-filled", getCompanyFieldCategory(profile.organization.name));
    options = [...options, ...companyOptions];
  }

  return {
    options,
    indexes: {
      companies, departments, interests, locations, skills, organizationValues
    }
  };
};

const Title = ({ storyQuestion, duplicate }) => {
  if (duplicate) return <Trans>Duplicate Story Prompt</Trans>;
  return storyQuestion
    ? <Trans>Edit Story Prompt</Trans>
    : <Trans>Create a Story Prompt</Trans>;
};

const StoryQuestionModal = ({
  children,
  storyQuestion,
  onCreate,
  onUpdate,
  selectedOptions,
  duplicate
}) => {
  const { profile: { organization: { name: orgName } } } = useProfile();
  const [changed, setChanged] = useState(false);
  const onChange = useCallback(() => setChanged(true), [setChanged]);
  const onConfirmClose = useCallback(
    () => {
      const message = t("You have unsaved changes, are you sure you'd like to close the prompt modal?");

      // eslint-disable-next-line no-alert
      if (!changed || window.confirm(message)) {
        restartState(false);
        onClose();
      }
    },
    [changed, onClose, restartState]
  );
  const labels = useFieldsLabels();
  const { open, onOpen, onClose } = useDoor(false);
  const { options: selectOptions } = useSelectOptions();
  const [tags, setTags] = useState(selectedOptions?.map(({ entity, entityType }) => (
    {
      category: getCategoryLabel(entityType, labels),
      value: entity?.id,
      label: entity?.name,
      categoryIcon: getIcon(entityType)
    }
  )) || []);
  const [scoreValue, setScoreValue] = useState(50);
  const [mappersValueIds, setMappersValueIds] = useState([]);
  const SCORE_MIN = 0;
  const SCORE_MAX = 1000;

  useEffect(
    () => {
      if (storyQuestion) {
        setScoreValue(storyQuestion.points ?? 50);
        setMappersValueIds(storyQuestion.externalMapperValueAssociations?.map(
          mapperValueAssocaition => (mapperValueAssocaition.externalMapperValue.id)
        ) || []);
      }
    }, [storyQuestion]
  );

  const restartState = useCallback(
    save => {
      if (!storyQuestion || !save) {
        setScoreValue(50);
        setTags(selectedOptions?.map(({ entity, entityType }) => (
          {
            category: getCategoryLabel(entityType, labels),
            value: entity?.id,
            label: entity?.name,
            categoryIcon: getIcon(entityType)
          }
        )) || []);
        setMappersValueIds([]);
      }
      setChanged(false);
    },
    [storyQuestion, selectedOptions, labels]
  );

  const [{ error }, onSubmit] = useTryCatch(
    async ({ allowedType, imageUrl, points, question, summary, visibility, status }) => {
      if (status === "hidden" && storyQuestion?.answers > 0 && storyQuestion?.status !== "hidden") {
        const confirmMessage = t(`Any associated stories in this ${storyQuestion.storyQuestionType === "parent_prompt" ? "trend" : "prompt"} will be removed from the Culture Page. Would you like to proceed?`);
        // eslint-disable-next-line no-alert
        if (!window.confirm(confirmMessage)) {
          onClose();
          return;
        }
      }

      if (status === "active" && storyQuestion?.answers > 0 && storyQuestion?.status === "archived") {
        const confirmMessage = t("In changing to Active status, any associated stories may now be moved out of Archive in the Admin Review tab");
        // eslint-disable-next-line no-alert
        if (!window.confirm(confirmMessage)) {
          onClose();
          return;
        }
      }

      if (status === "archived" && storyQuestion?.answers > 0 && storyQuestion?.status !== "archived") {
        const confirmMessage = t(`The stories in this ${storyQuestion.storyQuestionType === "parent_prompt" ? "trend" : "prompt"} will be archived automatically. Do you want to continue?`);
        // eslint-disable-next-line no-alert
        if (!window.confirm(confirmMessage)) {
          onClose();
          return;
        }
      }

      let newImageUrl = null;
      if (imageUrl && imageUrl.size) {
        newImageUrl = await signUpload(imageUrl);
      }

      if (storyQuestion?.status === "hidden" && status === "active") {
        const confirmMessage = t("In changing to Active status, any associated stories may now be selected for your Culture Page in the Admin Review tab");
        // eslint-disable-next-line no-alert
        if (!window.confirm(confirmMessage)) {
          return;
        }
      }

      const body = {
        allowedTypes: allowedType,
        imageUrl: newImageUrl,
        summary,
        points: points || scoreValue,
        question,
        visibility,
        status,
        mappersValueIds,
        tags: tags?.map(tag => {
          if (tag.category) {
            if (isRTX(orgName) && tag.category === tagsTypes.BUSINESSES) {
              return {
                ...tag,
                category: tagsTypes.COMPANIES
              };
            }
            const departmentCategories = [
              tagsTypes.BUSINESSES, tagsTypes.PROFESSIONS, tagsTypes.JOBFUNCTIONS
            ];
            return {
              ...tag,
              category: departmentCategories.includes(tag.category)
                ? tagsTypes.DEPARTMENTS : tag.category
            };
          }

          return tag;
        }) || []
      };
      if (storyQuestion && !duplicate) {
        const response = await makePatch(`/story_questions/${storyQuestion.id}`, body);
        onUpdate(response.storyQuestion);
      } else {
        const response = await makePost("/prompts", body);
        onCreate(response.storyQuestion);
      }

      restartState(true);
      onClose();
    },
    [storyQuestion, onClose, onCreate, onUpdate, tags, mappersValueIds]
  );

  if (error && error.status !== 422) {
    throw error;
  }

  const onSelectedGroupsChange = value => {
    onChange();
    setTags(value || []);
  };

  const handleSelectedJobMappings = ({ mappersValueIds }) => {
    setMappersValueIds(mappersValueIds || []);
  };

  return (
    <>
      {children(onOpen)}
      {open && (
        <ChqModalDialog onClose={onConfirmClose} className="story-question-modal">
          <ModalDialog.Heading onClose={onConfirmClose}>
            <Title storyQuestion={storyQuestion} duplicate={duplicate} />
          </ModalDialog.Heading>
          <ModalDialog.Body>
            {error && <ErrorMessage error={error} />}
            <Form
              onSubmit={onSubmit}
              initialValues={{
                visibility: "private_access",
                allowedType: storyQuestion?.allowedTypes || allowedMedia[0].value,
                status: "active",
                ...storyQuestion
              }}
            >
              <StringField name="question" autoFocus required onChange={onChange}>
                <Trans>Prompt</Trans>
              </StringField>
              <SelectField
                className="AllowedTypes"
                default
                options={allowedMedia}
                name="allowedType"
                required
                onChange={onChange}
              >
                <Trans>Media Types Allowed</Trans>
              </SelectField>
              <TextField name="summary" onChange={onChange}>
                <Trans>Instructions</Trans>
              </TextField>
              <NumberField
                name="points"
                required
                onChange={value => {
                  const numericValue = parseInt(value, 10);
                  if (!Number.isNaN(numericValue)) {
                    if ((numericValue >= SCORE_MIN)
                        && (numericValue <= SCORE_MAX)) {
                      setScoreValue(numericValue);
                      onChange();
                    }
                  }
                }}
                min={SCORE_MIN}
                max={SCORE_MAX}
                value={scoreValue.toString()}
              >
                <Trans>Score</Trans>
              </NumberField>
              <SelectField
                className="share-with-field"
                creatable
                name="allOptions"
                options={selectOptions}
                placeholder={t(`Select default ${labels.departments.plural.toLowerCase()}, locations, interests, etc.`)}
                multiple
                onChange={onSelectedGroupsChange}
                value={tags}
              >
                <Trans>Default Tags</Trans>
              </SelectField>
              {(!storyQuestion || storyQuestion?.storyQuestionType === "old_prompt") && (
                <SelectField
                  name="visibility"
                  required
                  options={[
                    { value: "private_access", label: t("Private") },
                    { value: "public_access", label: t("Public") }
                  ]}
                  onChange={onChange}
                >
                  <Trans>Visibility</Trans>
                </SelectField>
              )}
              <SelectField
                name="status"
                required
                options={[
                  { value: "active", label: t("Active") },
                  { value: "paused", label: t("Paused") },
                  { value: "hidden", label: t("Hidden") },
                  { value: "archived", label: t("Archived") }
                ]}
                onChange={onChange}
              >
                <Trans>Status</Trans>
              </SelectField>
              <div className="Footer-Buttons">
                <SubmitButton primary>
                  {submitting => (
                    submitting ? <Trans>Submitting...</Trans> : <Trans>Save</Trans>
                  )}
                </SubmitButton>
                <ApproveForJobMappingButton
                  mappersValueIds={mappersValueIds}
                  onUpdate={value => {
                    handleSelectedJobMappings(value);
                  }}
                  storyQuestion={storyQuestion}
                />
              </div>
            </Form>
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
    </>
  );
};

export default StoryQuestionModal;
